import React, { useState, useEffect } from 'react';
import { btcTreasuryData } from '../data/btcTreasuryData';
import cachedDataService from '../services/cachedDataService';
import realDataService from '../services/realDataService';
import { useAuth } from '../AuthContext';

const BitcoinTreasuryFeed = ({ onError }) => {
  const { user } = useAuth();
  const [livePrices, setLivePrices] = useState({});
  const [news, setNews] = useState([]);
  const [newCompanies, setNewCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [apiKeyForm, setApiKeyForm] = useState({ show: false, service: '', key: '' });
  const [errors, setErrors] = useState({ prices: null, news: null, companies: null });
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0, status: '' });
  const [retryAttempts, setRetryAttempts] = useState({ prices: 0, news: 0, companies: 0 });
  const [hasInitialized, setHasInitialized] = useState(false);

  // Top Bitcoin treasury companies to track
  const trackedTickers = ['MSTR', 'MARA', 'RIOT', 'COIN', 'TSLA', 'SQ', 'HUT', 'CLSK'];



  // Fetch cached live prices from background service
  const fetchLivePrices = async () => {
    try {
      console.log('📈 Fetching cached stock prices...');
      setFetchProgress({ current: 0, total: trackedTickers.length, status: 'Loading from cache...' });
      
      const result = await cachedDataService.fetchStockPrices();
      
      if (result.success && Object.keys(result.data).length > 0) {
        setLivePrices(result.data);
        setErrors(prev => ({ ...prev, prices: null }));
        setLastUpdate(result.lastUpdated ? new Date(result.lastUpdated) : new Date());
        setFetchProgress({ current: Object.keys(result.data).length, total: trackedTickers.length, status: 'Complete' });
        console.log(`✅ Loaded ${Object.keys(result.data).length} cached prices`);
        
        if (result.source === 'background-cache') {
          console.log('🚀 Data served from background service - ultra fast!');
        }
      } else {
        throw new Error(result.error || 'No cached price data available');
      }
    } catch (error) {
      console.error('❌ Error fetching cached prices:', error);
      setErrors(prev => ({ ...prev, prices: `Cache error: ${error.message}` }));
      setFetchProgress({ current: 0, total: trackedTickers.length, status: 'Error' });
      setLivePrices({});
      
      if (onError && !hasInitialized) {
        onError();
      }
    }
  };

  // Fetch cached news and companies from background service
  const fetchNewsAndCompanies = async () => {
    try {
      console.log('📰 Fetching cached news and companies...');
      const [newsResult, companiesResult] = await Promise.all([
        cachedDataService.fetchNews(),
        cachedDataService.detectNewTreasuryCompanies()
      ]);
      
      if (newsResult.success) {
        setNews(newsResult.data.slice(0, 10));
        setErrors(prev => ({ ...prev, news: null }));
        console.log(`✅ Loaded ${newsResult.data.length} cached news articles`);
        
        if (newsResult.source === 'background-cache') {
          console.log('🚀 News served from background service - ultra fast!');
        }
      } else {
        setNews([]);
        setErrors(prev => ({ ...prev, news: newsResult.error }));
      }
      
      if (companiesResult.success) {
        setNewCompanies(companiesResult.data.slice(0, 5));
        setErrors(prev => ({ ...prev, companies: null }));
        console.log(`✅ Loaded ${companiesResult.data.length} cached companies`);
      } else {
        setNewCompanies([]);
        setErrors(prev => ({ ...prev, companies: companiesResult.error }));
      }
    } catch (error) {
      console.error('Error fetching cached data:', error);
      setNews([]);
      setNewCompanies([]);
      setErrors(prev => ({ 
        ...prev, 
        news: `Cache error: ${error.message}`,
        companies: `Cache error: ${error.message}`
      }));
    }
  };

  // Handle API key submission
  const handleApiKeySubmit = (e) => {
    e.preventDefault();
    if (apiKeyForm.service && apiKeyForm.key) {
      realDataService.setApiKey(apiKeyForm.service, apiKeyForm.key);
      setApiKeyForm({ show: false, service: '', key: '' });
      // Refresh data with new API key
      fetchLivePrices();
      fetchNewsAndCompanies();
    }
  };

  // Initialize data with Safari mobile optimizations
  useEffect(() => {
    const initializeData = async () => {
      console.log('🚀 BitcoinTreasuryFeed: Starting data initialization...');
      setIsLoading(true);
      
      // Small delay for component mounting
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        // Fetch cached data quickly (no external API calls)
        console.log('📊 Loading cached data...');
        
        const initPromise = Promise.all([
          fetchLivePrices(),
          fetchNewsAndCompanies()
        ]);
        
        // Much shorter timeout since we're using cached data
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Cache loading timeout')), 5000)
        );
        
        await Promise.race([initPromise, timeoutPromise]);
        
        setHasInitialized(true);
        console.log('✅ BitcoinTreasuryFeed initialization completed successfully');
      } catch (error) {
        console.error('❌ Error during BitcoinTreasuryFeed initialization:', error);
        
        // Trigger app-level recovery if initialization fails
        if (onError && !hasInitialized) {
          console.log('🚨 Treasury Feed init failed - triggering app recovery');
          onError();
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Start initialization immediately since we use cached data
    const initTimer = setTimeout(() => {
      initializeData();
    }, 50);

    // Check for updated cached data every 30 seconds (background service updates every 3-15 minutes)
    const priceInterval = setInterval(() => {
      if (hasInitialized) {
        console.log('⏱️ Checking for updated cached prices...');
        fetchLivePrices();
      }
    }, 30000);

    // Check for updated cached news every 60 seconds
    const newsInterval = setInterval(() => {
      if (hasInitialized) {
        console.log('⏱️ Checking for updated cached news...');
        fetchNewsAndCompanies();
      }
    }, 60000);

    return () => {
      clearTimeout(initTimer);
      clearInterval(priceInterval);
      clearInterval(newsInterval);
    };
  }, []);

  const getChangeColor = (change) => {
    if (change > 0) return 'text-green-400';
    if (change < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const getImpactColor = (impact) => {
    if (impact === 'bullish') return 'text-green-400';
    if (impact === 'bearish') return 'text-red-400';
    return 'text-yellow-400';
  };

  if (isLoading) {
    return (
      <div className="w-full p-6 bg-gray-900 text-white rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold mb-4">Bitcoin Treasury Live Feed</h2>
        <div className="flex items-center justify-center h-40">
          <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2 text-gray-400">Loading live data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 bg-gray-900 text-white rounded-lg shadow-xl">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Bitcoin Treasury Live Feed</h2>
          <p className="text-sm text-gray-400 mt-1">Real-time data from Yahoo Finance & RSS feeds</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button
            onClick={() => {
              console.log('Manual refresh triggered');
              fetchLivePrices();
              fetchNewsAndCompanies();
            }}
            className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-green-600 hover:bg-green-700 rounded transition-colors whitespace-nowrap"
          >
            🔄 Refresh
          </button>
          {user && (
            <button
              onClick={() => setApiKeyForm({ show: !apiKeyForm.show, service: '', key: '' })}
              className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 rounded transition-colors whitespace-nowrap"
            >
              ⚙️ API Keys
            </button>
          )}
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <div className={`w-2 h-2 rounded-full ${isLoading ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}></div>
            <span>{isLoading ? 'Loading...' : `Updated: ${lastUpdate.toLocaleTimeString()}`}</span>
          </div>
        </div>
      </div>

      {apiKeyForm.show && user && (
        <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold mb-3">Configure Your Personal API Keys</h3>
          <p className="text-sm text-gray-400 mb-4">
            Add your own API keys for enhanced data access and higher rate limits:
          </p>
          <div className="mb-4 p-3 bg-gray-700 rounded text-xs">
            <strong className="text-yellow-400">Why add your own keys?</strong>
            <ul className="text-gray-300 mt-2 space-y-1">
              <li>• Get faster refresh rates and more frequent updates</li>
              <li>• Access to premium data features</li>
              <li>• No rate limiting during high traffic</li>
              <li>• Priority access to real-time data</li>
            </ul>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <strong className="text-blue-400">Recommended Free API Providers:</strong>
              <ul className="text-gray-400 mt-1">
                <li>• <a href="https://polygon.io/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Polygon.io</a> - Best for stock data</li>
                <li>• <a href="https://twelvedata.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Twelve Data</a> - Great free tier</li>
                <li>• <a href="https://finnhub.io/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Finnhub</a> - Fast & reliable</li>
                <li>• <a href="https://newsapi.org/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">NewsAPI</a> - For news feeds</li>
              </ul>
            </div>
            <div>
              <strong className="text-green-400">Current Data Access:</strong>
              <ul className="text-gray-400 mt-1">
                <li>• Basic tier with limited refresh rates</li>
                <li>• Shared rate limits with all users</li>
                <li>• May experience delays during peak times</li>
                <li>• Add your own keys for priority access</li>
              </ul>
            </div>
          </div>
          <form onSubmit={handleApiKeySubmit} className="flex gap-3">
            <select
              value={apiKeyForm.service}
              onChange={(e) => setApiKeyForm(prev => ({ ...prev, service: e.target.value }))}
              className="px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
            >
              <option value="">Select Service</option>
              <option value="polygon">Polygon.io (recommended)</option>
              <option value="twelve_data">Twelve Data</option>
              <option value="finnhub">Finnhub</option>
              <option value="alpha_vantage">Alpha Vantage</option>
              <option value="news_api">NewsAPI</option>
            </select>
            <input
              type="text"
              placeholder="Enter API Key"
              value={apiKeyForm.key}
              onChange={(e) => setApiKeyForm(prev => ({ ...prev, key: e.target.value }))}
              className="flex-1 px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
            >
              Add Key
            </button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Live Prices Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-300 border-b border-gray-700 pb-2">
            Live Stock Prices
          </h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {Object.keys(livePrices).length === 0 ? (
              <div className="text-center text-red-400 py-8">
                {errors.prices ? (
                  <div>
                    <p className="font-semibold">❌ Price Data Unavailable</p>
                    <p className="text-xs mt-2 text-gray-400">{errors.prices}</p>
                    <p className="text-xs mt-1 text-yellow-400">Configure API keys for better access</p>
                  </div>
                ) : (
                  <div>
                    <p>🔄 Fetching live prices...</p>
                    <p className="text-xs mt-2 text-gray-400">
                      {fetchProgress.status} ({fetchProgress.current}/{fetchProgress.total})
                    </p>
                    {fetchProgress.total > 0 && (
                      <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
                        <div 
                          className="bg-blue-600 h-1 rounded-full transition-all duration-300" 
                          style={{ width: `${(fetchProgress.current / fetchProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              Object.entries(livePrices).map(([ticker, price]) => (
                <div key={ticker} className="flex items-center justify-between p-3 bg-gray-800 rounded hover:bg-gray-750 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-blue-400">{ticker}</span>
                      <span className="text-xs text-gray-500">
                        {price.name?.substring(0, 20) || 'Company'}
                      </span>
                      <span className="text-xs text-gray-600">
                        {price.source}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-mono text-lg">
                      ${price.price?.toFixed(2) || 'N/A'}
                    </div>
                    <div className={`text-sm ${getChangeColor(price.change || 0)}`}>
                      {price.change >= 0 ? '+' : ''}
                      ${price.change?.toFixed(2) || '0'} ({!isNaN(price.changePercent) ? price.changePercent?.toFixed(2) : '0'}%)
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* News Feed Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-300 border-b border-gray-700 pb-2">
            Bitcoin Treasury News
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {news.length === 0 ? (
              <div className="text-center text-red-400 py-8">
                {errors.news ? (
                  <div>
                    <p className="font-semibold">📰 No Treasury News Available</p>
                    <p className="text-xs mt-2 text-gray-400">No corporate Bitcoin treasury news found at this time</p>
                    <p className="text-xs mt-1 text-gray-500">Only showing news about companies adding Bitcoin to their treasury</p>
                  </div>
                ) : (
                  <div>
                    <p>🔄 Searching for treasury news...</p>
                    <p className="text-xs mt-2 text-gray-400">Looking for corporate Bitcoin adoption stories</p>
                  </div>
                )}
              </div>
            ) : (
              news.slice(0, 6).map(item => (
                <div key={item.id} className="p-3 bg-gray-800 rounded hover:bg-gray-750 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-semibold text-blue-400 text-sm">
                      {item.ticker || 'GENERAL'}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${getImpactColor(item.impact)} bg-opacity-20`}>
                        {item.impact}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(item.timestamp).toLocaleDateString()} {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                  <h4 className="text-sm font-medium text-white mb-1">
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">
                      {item.title}
                    </a>
                  </h4>
                  <p className="text-xs text-gray-400">{item.summary}</p>
                  <p className="text-xs text-gray-600 mt-1">Source: {item.source}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* New BTC Treasury Companies */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-300 border-b border-gray-700 pb-2">
            🚨 New BTC Adopters
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {newCompanies.length === 0 ? (
              <div className="text-center text-red-400 py-8">
                {errors.companies ? (
                  <div>
                    <p className="font-semibold">❌ Company Detection Unavailable</p>
                    <p className="text-xs mt-2 text-gray-400">{errors.companies}</p>
                    <p className="text-xs mt-1 text-yellow-400">Requires news data to detect new adopters</p>
                  </div>
                ) : (
                  <div>
                    <p>🔄 Scanning for new adopters...</p>
                    <p className="text-xs mt-2 text-gray-400">Early detection system</p>
                  </div>
                )}
              </div>
            ) : (
              newCompanies.map((company, index) => (
                <div key={`${company.ticker}-${index}`} className="p-3 bg-yellow-900/20 border border-yellow-700 rounded">
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-semibold text-yellow-400 text-sm">
                      {company.ticker || 'NEW'}
                    </span>
                    <span className="text-xs text-yellow-500">
                      🔥 Early Alert
                    </span>
                  </div>
                  <h4 className="text-sm font-medium text-white mb-1">
                    {company.company || 'Unknown Company'}
                  </h4>
                  <p className="text-xs text-gray-400">{company.announcement}</p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">
                      Source: {company.source}
                    </span>
                    <span className="text-xs text-yellow-500">
                      {new Date(company.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-2">
            <div className="text-sm text-gray-400">Avg Change</div>
            <div className={`font-semibold ${getChangeColor(
              Object.values(livePrices).reduce((acc, price) => acc + price.changePercent, 0) / Object.keys(livePrices).length
            )}`}>
              {Object.keys(livePrices).length > 0 
                ? `${(Object.values(livePrices).reduce((acc, price) => acc + price.changePercent, 0) / Object.keys(livePrices).length).toFixed(2)}%`
                : '0.00%'
              }
            </div>
          </div>
          <div className="p-2">
            <div className="text-sm text-gray-400">Total Volume</div>
            <div className="font-semibold text-white">
              {Object.values(livePrices).reduce((acc, price) => acc + price.volume, 0).toLocaleString()}
            </div>
          </div>
          <div className="p-2">
            <div className="text-sm text-gray-400">Active Tickers</div>
            <div className="font-semibold text-blue-400">
              {Object.keys(livePrices).length}
            </div>
          </div>
          <div className="p-2">
            <div className="text-sm text-gray-400">News Updates</div>
            <div className="font-semibold text-green-400">
              {news.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BitcoinTreasuryFeed;